import { ModalAssistant } from '@mairistem/assistant';
import { Dimmer, Header, Loader } from '@mairistem/composants';
import {
	type Exercice,
	getExercices,
	Sens,
	useService,
} from '@mairistem/comptabilite';
import { getErrorMessage, useEtiquette } from '@mairistem/finances-utils';
import { isNewIdentifiant, newIdentifiant } from '@mairistem/tools';
import React from 'react';
import { toast } from 'react-toastify';
import AUTORISATION_MODAL_STEP from '../../constants/autorisation/autorisationModalStep';
import DECISION from '../../constants/common/decision';
import STORAGE_KEY from '../../constants/common/storage';
import type Composant from '../../interfaces/common/composant';
import type Financement from '../../interfaces/common/financement';
import type ProImputation from '../../interfaces/common/proImputation';
import type Projet from '../../interfaces/common/projet';
import { createProjet, getProjet, updateProjet } from '../../services/projet';
import {
	validateStepCreditsPaiementRessources,
	validateStepInformations,
} from '../../utils/autorisation';
import FinancementsList from './modal/FinancementsList';
import InformationsGenerales from './modal/InformationsGenerales';

interface AutorisationModalProps {
	decision: number;
	identifiantAutorisation?: number;
	onClose: () => void;
	onFinish: (autorisation: Projet) => void;
}

interface Step {
	dataTestId: string;
	required: boolean;
	title: string;
	updating: boolean;
	validated: boolean;
}

const DEFAULT_STEPS = [
	{
		dataTestId: 'boutonInformationsGeneralesStep',
		required: true,
		title: 'Informations générales',
		updating: false,
		validated: false,
	},
	{
		dataTestId: 'boutonCreditsPaiementStep',
		required: false,
		title: 'Crédits de paiement',
		updating: false,
		validated: false,
	},
	{
		dataTestId: 'boutonRessourceStep',
		required: false,
		title: 'Ressources',
		updating: false,
		validated: false,
	},
];

const defaultAutorisation = {
	code: '',
	composants: [],
	dateVote: null,
	description: '',
	financements: [],
	identifiant: newIdentifiant(),
	identifiantEtiquette: null,
	identifiantService: null,
	libelle: '',
	montantAutorisation: 0,
} as Projet;

const identifiantEntite = Number(sessionStorage.getItem(STORAGE_KEY.ID_ENTITE));
const identifiantEntiteCompta = Number(sessionStorage.getItem(STORAGE_KEY.ID_ENTITE_CPTA))

const AutorisationModal = ({
	decision,
	identifiantAutorisation,
	onClose,
	onFinish,
}: AutorisationModalProps) => {
	const [autorisation, setAutorisation] =
		React.useState<Projet>(defaultAutorisation);
	const [exercices, setExercices] = React.useState<Exercice[]>([]);
	const [hasEvenements, setHasEvenements] = React.useState(false);
	const [step, setStep] = React.useState<number>(0);
	const [steps, setSteps] = React.useState<Step[]>(DEFAULT_STEPS);

	const EtiquettesInput = useEtiquette(
		'/api/finances/vision/etiquettes',
		identifiantEntite,
	);
	const [ServiceCombobox, isServiceComboboxVisible, isLoading] = useService();

	const fetchAutorisation = async () => {
		try {
			const data = await getProjet(
				identifiantAutorisation,
				new URLSearchParams({
					withComposants: 'true',
					withFinancements: 'true',
					withProImputations: 'true',
					withLignesEngagement: 'true',
					withLignesMouvement: 'true',
				}),
			);
			setAutorisation(data);
			// Si on a une liste de composants, alors on active par defaut la checkbox des evenements
			if (data.composants.length) setHasEvenements(true);
		} catch {
			toast.error("Erreur lors de la récupération de l'autorisation");
		}
	};

	const fetchExercices = async () => {
		try {
			const { data } = await getExercices(
				new URLSearchParams(
					`all=true&identifiantEntite=${sessionStorage.getItem(STORAGE_KEY.ID_ENTITE_CPTA)}`,
				),
			);
			setExercices(data);
		} catch {
			toast.error('Erreur lors de la récupération des exercices');
		}
	};

	const handleSaveStep = (index: number) => {
		let stepResult = null;

		switch (index) {
			case AUTORISATION_MODAL_STEP.INFORMATIONS_GENERALES:
				stepResult = validateStepInformations(
					autorisation,
					isServiceComboboxVisible,
				);
				break;
			case AUTORISATION_MODAL_STEP.CREDITS_PAIEMENT:
				stepResult = validateStepCreditsPaiementRessources(
					autorisation,
					hasEvenements,
					Sens.DEPENSE,
				);
				break;
			case AUTORISATION_MODAL_STEP.RESSOURCES:
				stepResult = validateStepCreditsPaiementRessources(
					autorisation,
					hasEvenements,
					Sens.RECETTE,
				);
				break;
			default:
				return true;
		}

		setSteps((old) =>
			old.map((oldStep, i) => {
				if (i !== index) return oldStep;

				return { ...oldStep, ...stepResult };
			}),
		);

		return stepResult.validated;
	};

	const handleChangeStep = (newStep: number) => {
		setStep((old) => {
			handleSaveStep(old);

			return newStep;
		});

		// Tri des financements via l'exercice
		setAutorisation((old) => ({
			...old,
			...(hasEvenements
				? {
						composants: old.composants.map((composant) => ({
							...composant,
							financements: composant.financements.sort(
								(a, b) => a.exercice - b.exercice,
							),
						})),
					}
				: {
						financements: old.financements.sort(
							(a, b) => a.exercice - b.exercice,
						),
					}),
		}));
	};

	const handleFinish = async () => {
		// On verifie si toutes les etapes sont valides
		const hasStepsInvalid = steps.some(({ updating, validated }, i) => {
			// Si l'etape a ete mise a jour mais qu'elle n'est pas valide
			if (updating && !validated) {
				setStep(i);
				// Permet d'afficher le bandeau d'erreur si elle n'etait deja pas presente (arrive quand on finish sur une etape optionnelle non valide)
				handleSaveStep(i);
				return true;
			}

			return false;
		});

		if (!hasStepsInvalid) {
			// On est en modification
			if (identifiantAutorisation) {
				try {
					const data = await updateProjet(
						identifiantAutorisation,
						autorisation,
						identifiantEntiteCompta,
					);
					onFinish(data);
				} catch (e) {
					toast.error(
						getErrorMessage(
							e,
							"Erreur lors de la modification de l'autorisation",
						),
					);
				}
			} else {
				// En creation
				try {
					const data = await createProjet({ ...autorisation, decision, identifiantEntiteCompta },);
					onFinish(data);
				} catch (e) {
					toast.error(
						getErrorMessage(e, "Erreur lors de la création de l'autorisation"),
					);
				}
			}
		}
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: Pas besoin de 'fetchAutorisation' et 'fetchExercices'
	React.useEffect(() => {
		if (identifiantAutorisation) {
			fetchAutorisation();
		}
		fetchExercices();
	}, [identifiantAutorisation]);

	const handleStepUpdating = () => {
		setSteps((old) =>
			old.map((oldStep, i) => {
				if (i === step) {
					return {
						...oldStep,
						updating: true,
						validated: false,
					};
				}

				return oldStep;
			}),
		);
	};

	const handleAddComposant = () => {
		handleStepUpdating();

		setAutorisation((old) => ({
			...old,
			composants: [
				...old.composants,
				{
					description: '',
					financements: [],
					identifiant: newIdentifiant(),
					libelle: '',
					montantEvenement: 0,
				} as Composant,
			],
		}));
	};

	const handleAddFinancement = (
		sens: string,
		identifiantComposant?: number | string,
	) => {
		const newData = {
			identifiant: newIdentifiant(),
			montant: 0,
			proImputations: [],
			rarN: 0,
			rarN1: 0,
			revision: 0,
			sens,
		} as Financement;

		handleStepUpdating();

		setAutorisation((old) => ({
			...old,
			...(hasEvenements
				? {
						composants: old.composants.map((composant) =>
							composant.identifiant === identifiantComposant
								? {
										...composant,
										financements: [newData, ...composant.financements],
									}
								: composant,
						),
					}
				: { financements: [newData, ...(old.financements ?? [])] }),
		}));
	};

	const handleAddProImputation = (
		identifiantFinancement: number | string,
		identifiantComposant: number | string,
		sens: string,
	) => {
		const newData = {
			identifiant: newIdentifiant(),
			lignesEngagement: [],
			lignesMouvement: [],
			montant: 0,
			rarN: 0,
			rarN1: 0,
			revision: 0,
			sens,
		} as ProImputation;

		handleStepUpdating();

		setAutorisation((old) => ({
			...old,
			...(hasEvenements
				? {
						composants: old.composants.map((composant) =>
							composant.identifiant === identifiantComposant
								? {
										...composant,
										financements: composant.financements.map((financement) =>
											financement.identifiant === identifiantFinancement
												? {
														...financement,
														proImputations: [
															...financement.proImputations,
															newData,
														],
													}
												: financement,
										),
									}
								: composant,
						),
					}
				: {
						financements: old.financements.map((financement) =>
							financement.identifiant === identifiantFinancement
								? {
										...financement,
										proImputations: [...financement.proImputations, newData],
									}
								: financement,
						),
					}),
		}));
	};

	const handleChangeAutorisation = (data: Partial<Projet>) => {
		handleStepUpdating();

		setAutorisation((old) => ({ ...old, ...data }));
	};

	const handleChangeComposant = (
		identifiantEvenement: number | string,
		data: Partial<Composant>,
	) => {
		handleStepUpdating();

		setAutorisation((old) => ({
			...old,
			composants: old.composants.map((composant) => ({
				...composant,
				...(identifiantEvenement === composant.identifiant && data),
			})),
		}));
	};

	const handleChangeFinancement = (
		data: Partial<Financement>,
		identifiantFinancement: number | string,
		identifiantComposant?: number | string,
	) => {
		handleStepUpdating();

		setAutorisation((old) => ({
			...old,
			...(hasEvenements
				? {
						composants: old.composants.map((composant) =>
							composant.identifiant === identifiantComposant
								? {
										...composant,
										financements: composant.financements.map((financement) =>
											financement.identifiant === identifiantFinancement
												? { ...financement, ...data }
												: financement,
										),
									}
								: composant,
						),
					}
				: {
						financements: old.financements.map((financement) =>
							financement.identifiant === identifiantFinancement
								? { ...financement, ...data }
								: financement,
						),
					}),
		}));
	};

	const handleChangeHasEvenements = () => {
		setHasEvenements((old) => {
			// Si il n'existe pas d'evenement, on en rajoute un par defaut
			if (!old && autorisation.composants.length === 0) {
				handleAddComposant();
			} else {
				handleStepUpdating();
			}

			return !old;
		});
	};

	const handleChangeProImputation = (
		data: Partial<ProImputation>,
		identifiantProImputation: number | string,
		identifiantFinancement: number | string,
		identifiantComposant: number | string,
	) => {
		handleStepUpdating();

		setAutorisation((old) => ({
			...old,
			...(hasEvenements
				? {
						composants: old.composants.map((composant) =>
							composant.identifiant === identifiantComposant
								? {
										...composant,
										financements: composant.financements.map((financement) =>
											financement.identifiant === identifiantFinancement
												? {
														...financement,
														proImputations: financement.proImputations.map(
															(proImputation) =>
																proImputation.identifiant ===
																identifiantProImputation
																	? {
																			...proImputation,
																			...data,
																		}
																	: proImputation,
														),
													}
												: financement,
										),
									}
								: composant,
						),
					}
				: {
						financements: old.financements.map((financement) =>
							financement.identifiant === identifiantFinancement
								? {
										...financement,
										proImputations: financement.proImputations.map(
											(proImputation) =>
												proImputation.identifiant === identifiantProImputation
													? {
															...proImputation,
															...data,
														}
													: proImputation,
										),
									}
								: financement,
						),
					}),
		}));
	};

	const handleRemoveComposant = (identifiantComposant: number | string) => {
		handleStepUpdating();

		setAutorisation((old) => ({
			...old,
			composants: old.composants.filter(
				(composant) => composant.identifiant !== identifiantComposant,
			),
			...(!isNewIdentifiant(identifiantComposant) && {
				deletedComposants: [
					...(old.deletedComposants ?? []),
					identifiantComposant as number,
				],
			}),
		}));
	};

	const handleRemoveFinancement = (
		identifiantFinancement: number | string,
		identifiantComposant?: number | string,
	) => {
		handleStepUpdating();

		setAutorisation((old) => ({
			...old,
			...(!isNewIdentifiant(identifiantFinancement) && {
				deletedFinancements: [
					...(old.deletedFinancements ?? []),
					identifiantFinancement as number,
				],
			}),
			...(hasEvenements
				? {
						composants: old.composants.map((composant) =>
							composant.identifiant === identifiantComposant
								? {
										...composant,
										financements: composant.financements.filter(
											(financement) =>
												financement.identifiant !== identifiantFinancement,
										),
									}
								: composant,
						),
					}
				: {
						financements: old.financements.filter(
							(financement) =>
								financement.identifiant !== identifiantFinancement,
						),
					}),
		}));
	};

	const handleRemoveProImputation = (
		identifiantProImputation: number | string,
		identifiantFinancement: number | string,
		identifiantComposant?: number | string,
	) => {
		handleStepUpdating();

		setAutorisation((old) => ({
			...old,
			...(!isNewIdentifiant(identifiantProImputation) && {
				deletedProImputations: [
					...(old.deletedProImputations ?? []),
					identifiantProImputation as number,
				],
			}),
			...(hasEvenements
				? {
						composants: old.composants.map((composant) =>
							composant.identifiant === identifiantComposant
								? {
										...composant,
										financements: composant.financements.map((financement) =>
											financement.identifiant === identifiantFinancement
												? {
														...financement,
														proImputations: financement.proImputations.filter(
															(proImputation) =>
																proImputation.identifiant !==
																identifiantProImputation,
														),
													}
												: financement,
										),
									}
								: composant,
						),
					}
				: {
						financements: old.financements.map((financement) =>
							financement.identifiant === identifiantFinancement
								? {
										...financement,
										proImputations: financement.proImputations.filter(
											(proImputation) =>
												proImputation.identifiant !== identifiantProImputation,
										),
									}
								: financement,
						),
					}),
		}));
	};

	// Permet de connaitre les exercices ayant la gestion avec TVA
	const exercicesGestionTva = React.useMemo(
		() =>
			exercices.reduce((acc, curr) => {
				if (curr.typeCompta === 2) acc.add(curr.anneeExercice);

				return acc;
			}, new Set<number>()),
		[exercices],
	);

	// Permet de connaitre les exercices ayant les reports au fonctionnement
	const exercicesReportFonc = React.useMemo(
		() =>
			exercices.reduce((acc, curr) => {
				if (curr.budReportFonc) acc.add(curr.anneeExercice);

				return acc;
			}, new Set<number>()),
		[exercices],
	);

	return (
		<ModalAssistant
			autoUpdating={false}
			currentStep={step}
			data-testid="autorisationModal"
			heightContent={window.innerHeight * 0.6}
			initialStep={AUTORISATION_MODAL_STEP.INFORMATIONS_GENERALES}
			onClose={onClose}
			onFinish={handleFinish}
			onSaveStep={handleSaveStep}
			onStepChange={handleChangeStep}
			open
			size="massive"
			steps={steps}
			title={
				<Header
					content={`${identifiantAutorisation ? 'Modifier' : 'Créer'} une autorisation ${decision === DECISION.AP ? 'de programme' : "d'engagement"}`}
					size="small"
					subheader={autorisation.code}
				/>
			}
		>
			<ModalAssistant.Content>
				{autorisation && !isLoading ? (
					<>
						{step === AUTORISATION_MODAL_STEP.INFORMATIONS_GENERALES && (
							<InformationsGenerales
								autorisation={autorisation}
								EtiquettesInput={EtiquettesInput}
								hasEvenements={hasEvenements}
								isServiceComboboxVisible={isServiceComboboxVisible}
								onAddComposant={handleAddComposant}
								onChangeAutorisation={handleChangeAutorisation}
								onChangeComposant={handleChangeComposant}
								onChangeHasEvenements={handleChangeHasEvenements}
								onRemoveComposant={handleRemoveComposant}
								ServiceCombobox={ServiceCombobox}
							/>
						)}
						{step === AUTORISATION_MODAL_STEP.CREDITS_PAIEMENT && (
							<FinancementsList
								autorisation={autorisation}
								decision={decision}
								exercices={exercices}
								exercicesGestionTva={exercicesGestionTva}
								exercicesReportFonc={exercicesReportFonc}
								hasEvenements={hasEvenements}
								onAddFinancement={(identifiantFinancement) =>
									handleAddFinancement(Sens.DEPENSE, identifiantFinancement)
								}
								onAddProImputation={(
									identifiantFinancement,
									identifiantComposant,
								) =>
									handleAddProImputation(
										identifiantFinancement,
										identifiantComposant,
										Sens.DEPENSE,
									)
								}
								onChangeFinancement={handleChangeFinancement}
								onChangeProImputation={handleChangeProImputation}
								onRemoveFinancement={handleRemoveFinancement}
								onRemoveProImputation={handleRemoveProImputation}
								sens={Sens.DEPENSE}
							/>
						)}
						{step === AUTORISATION_MODAL_STEP.RESSOURCES && (
							<FinancementsList
								autorisation={autorisation}
								decision={decision}
								exercices={exercices}
								exercicesGestionTva={exercicesGestionTva}
								exercicesReportFonc={exercicesReportFonc}
								hasEvenements={hasEvenements}
								onAddFinancement={(identifiantFinancement) =>
									handleAddFinancement(Sens.RECETTE, identifiantFinancement)
								}
								onAddProImputation={(
									identifiantFinancement,
									identifiantComposant,
								) =>
									handleAddProImputation(
										identifiantFinancement,
										identifiantComposant,
										Sens.RECETTE,
									)
								}
								onChangeFinancement={handleChangeFinancement}
								onChangeProImputation={handleChangeProImputation}
								onRemoveFinancement={handleRemoveFinancement}
								onRemoveProImputation={handleRemoveProImputation}
								sens={Sens.RECETTE}
							/>
						)}
					</>
				) : (
					<Dimmer active inverted>
						<Loader size="big" />
					</Dimmer>
				)}
			</ModalAssistant.Content>
		</ModalAssistant>
	);
};

export default AutorisationModal;
